"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Info,
  RotateCw,
  Search,
  Sprout,
  ZoomIn,
  CircleCheck,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { isRecipePulled } from "@/lib/commerce/recipe-pull";
import type {
  AlternativeOption,
  DesignPlant,
  MissingPlant,
  PlantFacts,
  RecipeAlternativesResponse,
} from "@/lib/substitution/alternatives-api";

/**
 * Out-of-stock alternatives picker.
 *
 * Shown after a shopper picks a style (recipe) whose plants are partly sold
 * out in the store's Shopify — or at Generate time when something sold out
 * since browsing. The parent owns the stock read; this component owns only
 * the presentation and the shopper's per-plant choice.
 *
 * Phases: checking (parent loading) → searching (short reveal of results that
 * are already computed) → review (pick one replacement per sold-out plant),
 * plus error when stock couldn't be verified.
 */

export type AlternativesChoice = Record<
  string /* missing plantId */,
  DesignPlant | null /* null = continue without it */
>;

export interface PlantAlternativesDialogProps {
  isOpen: boolean;
  /** "loading" while the parent's fresh stock read is in flight; "ready" with `result`; "error" when stock couldn't be verified. */
  status: "loading" | "ready" | "error";
  result: RecipeAlternativesResponse | null;
  recipeName: string;
  /** e.g. "Plantista" — used in "Checking availability at {storeName}…". Optional. */
  storeName?: string;
  /** "select" = right after picking a style; "gate" = a plant sold out between browsing and Generate (copy: "Some plants just sold out"). */
  trigger: "select" | "gate";
  onConfirm: (choice: AlternativesChoice) => void;
  /** Back to the style list (choose another style). */
  onBack: () => void;
  onRetry: () => void;
}

type Phase = "checking" | "searching" | "review" | "error";

/** Selected alternative plant id per missing plantId; null = without it. */
type SelectionMap = Record<string, string | null>;

const STEP_MS = 400;
const MAX_SEARCH_MS = 2000;

// ── reduced motion ──────────────────────────────────────────────────────────

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(cb: () => void) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}

function getReducedMotion() {
  return typeof window !== "undefined" && !!window.matchMedia
    ? window.matchMedia(REDUCED_MOTION_QUERY).matches
    : false;
}

function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotion,
    () => false
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

function plural(n: number, one: string, many: string) {
  return n === 1 ? one : many;
}

function formatPrice(price: number | null): string | null {
  if (typeof price !== "number" || !Number.isFinite(price)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(price);
}

/** defaultId → first unused alternative → without. Never reuses a substitute. */
function initialSelections(missing: MissingPlant[]): SelectionMap {
  const used = new Set<string>();
  const out: SelectionMap = {};
  for (const m of missing) {
    const ids = m.alternatives.map((a) => a.plant.id);
    let pick: string | null = null;
    if (m.defaultId && ids.includes(m.defaultId) && !used.has(m.defaultId)) {
      pick = m.defaultId;
    } else {
      pick = ids.find((id) => !used.has(id)) ?? null;
    }
    if (pick) used.add(pick);
    out[m.plantId] = pick;
  }
  return out;
}

const THUMB_BOX = {
  sm: "w-10 h-10",
  md: "w-12 h-12",
  lg: "w-16 h-16",
  xl: "w-[72px] h-[72px]",
} as const;

function Thumb({
  src,
  alt,
  size = "md",
  onClick,
  clickLabel,
}: {
  src: string | null;
  alt: string;
  size?: keyof typeof THUMB_BOX;
  /** Makes the image a button (opens the compare view). */
  onClick?: () => void;
  clickLabel?: string;
}) {
  const [failed, setFailed] = useState(false);
  const box = THUMB_BOX[size];
  const image =
    !src || failed ? (
      <div
        className={cn(
          box,
          "shrink-0 rounded-lg bg-plantista-100 dark:bg-plantista-800 text-plantista-500 dark:text-plantista-300 flex items-center justify-center"
        )}
        role={onClick ? undefined : "img"}
        aria-label={onClick ? undefined : alt}
      >
        <Sprout className="w-5 h-5" aria-hidden="true" />
      </div>
    ) : (
      // eslint-disable-next-line @next/next/no-img-element -- remote plant images, matches codebase usage
      <img
        src={src}
        alt={onClick ? "" : alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className={cn(
          box,
          "shrink-0 rounded-lg object-cover bg-plantista-100 dark:bg-plantista-800"
        )}
      />
    );
  if (!onClick) return image;
  return (
    <button
      type="button"
      onClick={(e) => {
        // Inside a <label>: don't also toggle the radio.
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      aria-label={clickLabel ?? `Compare ${alt}`}
      title="Compare plants"
      className="group/thumb relative shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 dark:focus-visible:ring-plantista-300"
    >
      {image}
      <span
        aria-hidden="true"
        className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-plantista-700 shadow-sm transition group-hover/thumb:scale-110 dark:bg-plantista-950/90 dark:text-plantista-200"
      >
        <ZoomIn className="h-3 w-3" />
      </span>
    </button>
  );
}

// ── component ───────────────────────────────────────────────────────────────

export function PlantAlternativesDialog({
  isOpen,
  status,
  result,
  recipeName,
  storeName,
  trigger,
  onConfirm,
  onBack,
  onRetry,
}: PlantAlternativesDialogProps) {
  const reducedMotion = usePrefersReducedMotion();
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Internal state is tied to the (isOpen, result) pair it was built for and
  // reset during render when the dialog reopens, a new result arrives, or the
  // status turns ready again (React's "adjust state on prop
  // change" pattern — no effect round-trip, no stale flash).
  const [revealed, setRevealed] = useState(0);
  const [selections, setSelections] = useState<SelectionMap>(() =>
    result ? initialSelections(result.missing) : {}
  );
  const [trackedResult, setTrackedResult] = useState(result);
  const [trackedOpen, setTrackedOpen] = useState(isOpen);
  const [trackedStatus, setTrackedStatus] = useState(status);
  // Compare view: one sold-out plant side by side with one of its options.
  const [compare, setCompare] = useState<{
    missingId: string;
    altId: string | null;
  } | null>(null);

  if (
    trackedResult !== result ||
    trackedOpen !== isOpen ||
    trackedStatus !== status
  ) {
    const reopened = isOpen && !trackedOpen;
    const becameReady = status === "ready" && trackedStatus !== "ready";
    setTrackedOpen(isOpen);
    setTrackedStatus(status);
    if (trackedResult !== result || reopened || becameReady) {
      setTrackedResult(result);
      setRevealed(0);
      setSelections(result ? initialSelections(result.missing) : {});
      setCompare(null);
    }
  }

  const missing = useMemo(() => result?.missing ?? [], [result]);
  const n = missing.length;
  const stepMs =
    n > 0 ? Math.min(STEP_MS, Math.floor(MAX_SEARCH_MS / (n + 1))) : 0;

  let phase: Phase;
  if (status === "loading") phase = "checking";
  else if (status === "error" || !result) phase = "error";
  else if (n > 0 && !reducedMotion && revealed <= n) phase = "searching";
  else phase = "review";

  // Searching: advance one step per missing plant, then one more tick to land
  // on review. Timers are cleared on close/unmount/result change.
  useEffect(() => {
    if (!isOpen || phase !== "searching") return;
    const t = window.setTimeout(() => setRevealed((r) => r + 1), stepMs);
    return () => window.clearTimeout(t);
  }, [isOpen, phase, revealed, stepMs]);

  // Focus the title on open and when the shopper lands on an actionable phase.
  const focusPhase = phase === "searching" ? "checking" : phase;
  useEffect(() => {
    if (!isOpen) return;
    titleRef.current?.focus();
    // Re-focus when the compare view opens or closes (its own title).
  }, [isOpen, focusPhase, compare?.missingId]);

  // Escape = back to styles, only where the shopper can act.
  useEffect(() => {
    if (!isOpen || (phase !== "review" && phase !== "error")) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (compare) setCompare(null);
        else onBack();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, phase, onBack, compare]);

  const select = useCallback((plantId: string, altId: string | null) => {
    setSelections((prev) => ({ ...prev, [plantId]: altId }));
  }, []);

  // Which missing plant currently holds each substitute id.
  const holderOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const [plantId, altId] of Object.entries(selections)) {
      if (altId) map.set(altId, plantId);
    }
    return map;
  }, [selections]);

  const swaps = missing.filter((m) => selections[m.plantId]).length;
  const total = result?.total ?? 0;
  const covered = Math.min(total, (result?.available ?? 0) + swaps);
  const pulled = result
    ? isRecipePulled(covered, total, result.overridden)
    : false;

  const handleConfirm = () => {
    if (!result || pulled) return;
    const choice: AlternativesChoice = {};
    for (const m of missing) {
      const altId = selections[m.plantId] ?? null;
      choice[m.plantId] = altId
        ? (m.alternatives.find((a) => a.plant.id === altId)?.plant ?? null)
        : null;
    }
    onConfirm(choice);
  };

  if (!isOpen) return null;

  const titleId = "plant-alternatives-title";
  const titleClass =
    "text-xl font-bold font-display text-plantista-950 dark:text-white outline-none";
  const subtleText =
    "text-sm text-plantista-600 dark:text-plantista-300 leading-relaxed";

  const soldOutLine = `${n} ${plural(n, "plant", "plants")} ${plural(n, "is", "are")} sold out.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — click = back to styles only where Escape would also work */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        aria-hidden="true"
        onClick={
          phase === "review" && compare
            ? () => setCompare(null)
            : phase === "review" || phase === "error"
              ? onBack
              : undefined
        }
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={phase === "checking" || phase === "searching"}
        className="relative flex flex-col w-full max-w-2xl max-h-[90vh] rounded-2xl bg-white dark:bg-plantista-900 shadow-2xl border border-gray-200 dark:border-plantista-800 animate-in zoom-in-95 duration-200 overflow-hidden"
      >
        {/* ── checking ─────────────────────────────────────────────── */}
        {phase === "checking" && (
          <div className="p-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-plantista-100 dark:bg-plantista-800 text-plantista-600 dark:text-plantista-300 mb-4">
              <Search className="w-6 h-6" aria-hidden="true" />
            </div>
            <h2
              id={titleId}
              ref={titleRef}
              tabIndex={-1}
              className={titleClass}
            >
              Checking availability…
            </h2>
            <p className={cn(subtleText, "mt-1")}>
              {storeName ? `at ${storeName} · ` : ""}
              {recipeName}
            </p>
            <div
              role="progressbar"
              aria-label="Checking plant availability"
              aria-valuetext="Checking"
              className="relative mt-5 h-2 w-full overflow-hidden rounded-full bg-plantista-100 dark:bg-plantista-800"
            >
              <div className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-plantista-500 dark:bg-plantista-400 animate-shimmer motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-60" />
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={onBack}
                className="text-sm font-medium text-plantista-600 dark:text-plantista-300 hover:text-plantista-950 dark:hover:text-white underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 rounded"
              >
                Back to styles
              </button>
            </div>
          </div>
        )}

        {/* ── searching ────────────────────────────────────────────── */}
        {phase === "searching" && (
          <div className="p-6">
            <h2
              id={titleId}
              ref={titleRef}
              tabIndex={-1}
              className={titleClass}
            >
              {trigger === "gate"
                ? "Some plants just sold out"
                : "Finding alternatives"}
            </h2>
            <p className={cn(subtleText, "mt-1")} aria-live="polite">
              {soldOutLine} Looking for alternatives…
            </p>
            <div
              role="progressbar"
              aria-label="Looking for alternatives"
              aria-valuemin={0}
              aria-valuemax={n}
              aria-valuenow={Math.min(revealed, n)}
              className="mt-5 h-2 w-full overflow-hidden rounded-full bg-plantista-100 dark:bg-plantista-800"
            >
              <div
                className="h-full rounded-full bg-plantista-500 dark:bg-plantista-400 transition-[width] ease-out"
                style={{
                  width: `${(Math.min(revealed, n) / n) * 100}%`,
                  transitionDuration: `${stepMs}ms`,
                }}
              />
            </div>
            <ul className="mt-4 space-y-1.5" aria-live="polite">
              {missing.slice(0, revealed).map((m) => {
                const k = m.alternatives.length;
                return (
                  <li
                    key={m.plantId}
                    className="flex items-center gap-2 text-sm animate-in fade-in duration-200"
                  >
                    {k > 0 ? (
                      <>
                        <Check
                          className="w-4 h-4 shrink-0 text-plantista-600 dark:text-plantista-400"
                          aria-hidden="true"
                        />
                        <span className="text-plantista-800 dark:text-plantista-100">
                          Found {k} {plural(k, "option", "options")} for{" "}
                          {m.name}
                        </span>
                      </>
                    ) : (
                      <>
                        <span
                          className="w-4 h-4 shrink-0 flex items-center justify-center text-gray-400 dark:text-plantista-500"
                          aria-hidden="true"
                        >
                          –
                        </span>
                        <span className="text-plantista-600 dark:text-plantista-300">
                          No close match for {m.name}
                        </span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── compare (from a clicked image in review) ─────────────── */}
        {phase === "review" &&
          result &&
          compare &&
          (() => {
            const m = missing.find((x) => x.plantId === compare.missingId);
            if (!m) return null;
            const alt =
              m.alternatives.find((a) => a.plant.id === compare.altId) ?? null;
            const holder = alt ? holderOf.get(alt.plant.id) : undefined;
            return (
              <CompareView
                titleId={titleId}
                titleRef={titleRef}
                missing={m}
                alt={alt}
                isSelected={!!alt && selections[m.plantId] === alt.plant.id}
                takenBy={
                  holder !== undefined && holder !== m.plantId
                    ? (missing.find((x) => x.plantId === holder)?.name ??
                      "another plant")
                    : null
                }
                onPickAlt={(altId) =>
                  setCompare({ missingId: m.plantId, altId })
                }
                onChoose={() => {
                  if (alt) select(m.plantId, alt.plant.id);
                  setCompare(null);
                }}
                onClose={() => setCompare(null)}
              />
            );
          })()}

        {/* ── review ───────────────────────────────────────────────── */}
        {phase === "review" && result && !compare && (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-plantista-800">
              <h2
                id={titleId}
                ref={titleRef}
                tabIndex={-1}
                className={titleClass}
              >
                {trigger === "gate"
                  ? "Some plants just sold out"
                  : "Pick replacements"}
              </h2>
              <p className={cn(subtleText, "mt-1")}>
                {n > 0
                  ? `${soldOutLine} Choose an in-stock alternative for each one in ${recipeName}, or continue without it.`
                  : `Everything in ${recipeName} is in stock.`}
              </p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
              {missing.map((m) => (
                <MissingPlantGroup
                  key={m.plantId}
                  missing={m}
                  selected={selections[m.plantId] ?? null}
                  holderOf={holderOf}
                  nameOf={(id) =>
                    missing.find((x) => x.plantId === id)?.name ??
                    "another plant"
                  }
                  onSelect={select}
                  onCompare={(altId) =>
                    setCompare({ missingId: m.plantId, altId })
                  }
                />
              ))}
            </div>

            <div className="border-t border-gray-200 dark:border-plantista-800 bg-white dark:bg-plantista-900 px-6 py-4">
              {pulled && (
                <div
                  role="status"
                  className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
                >
                  <AlertTriangle
                    className="w-4 h-4 mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>
                    This style needs at least half of its plants. Pick
                    alternatives for more plants, or choose another style.
                  </span>
                </div>
              )}
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="w-full sm:w-auto"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
                  Back to styles
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleConfirm}
                  disabled={pulled}
                  className="w-full sm:w-auto font-bold"
                >
                  {swaps > 0
                    ? `Continue with ${swaps} ${plural(swaps, "swap", "swaps")}`
                    : "Continue"}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ── error ────────────────────────────────────────────────── */}
        {phase === "error" && (
          <div className="p-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 mb-4">
              <AlertTriangle className="w-6 h-6" aria-hidden="true" />
            </div>
            <h2
              id={titleId}
              ref={titleRef}
              tabIndex={-1}
              className={titleClass}
            >
              We couldn&apos;t check stock right now
            </h2>
            <p className={cn(subtleText, "mt-2")}>
              The store didn&apos;t answer. Try again, or pick another style.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
                Back to styles
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={onRetry}
                className="w-full sm:w-auto font-bold"
              >
                <RotateCw className="w-4 h-4 mr-2" aria-hidden="true" />
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── review card ─────────────────────────────────────────────────────────────

const optionRow =
  "flex items-start gap-3 rounded-xl border-2 px-3 py-2.5 transition cursor-pointer " +
  "border-gray-200 dark:border-plantista-700 bg-white dark:bg-plantista-900 " +
  "hover:border-plantista-300 dark:hover:border-plantista-600 " +
  "has-[:checked]:border-plantista-500 dark:has-[:checked]:border-plantista-400 " +
  "has-[:checked]:bg-plantista-50 dark:has-[:checked]:bg-plantista-800/60 " +
  "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-plantista-500 dark:has-[:focus-visible]:ring-plantista-300 " +
  "has-[:disabled]:opacity-50 has-[:disabled]:cursor-not-allowed has-[:disabled]:hover:border-gray-200 dark:has-[:disabled]:hover:border-plantista-700";

function RadioDot({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 self-center",
        checked
          ? "border-plantista-600 dark:border-plantista-400"
          : "border-gray-300 dark:border-plantista-600"
      )}
    >
      {checked && (
        <span className="h-2 w-2 rounded-full bg-plantista-600 dark:bg-plantista-400" />
      )}
    </span>
  );
}

function MissingPlantGroup({
  missing,
  selected,
  holderOf,
  nameOf,
  onSelect,
  onCompare,
}: {
  missing: MissingPlant;
  selected: string | null;
  holderOf: Map<string, string>;
  nameOf: (plantId: string) => string;
  onSelect: (plantId: string, altId: string | null) => void;
  /** Open the compare view against this option (null = no options). */
  onCompare: (altId: string | null) => void;
}) {
  const groupName = `alt-${missing.plantId}`;
  const headingId = `${groupName}-heading`;
  const hasAlternatives = missing.alternatives.length > 0;

  return (
    <section
      role="radiogroup"
      aria-labelledby={headingId}
      className="rounded-2xl border border-gray-200 dark:border-plantista-800 bg-gray-50/60 dark:bg-plantista-950/40 p-4"
    >
      <div className="flex items-center gap-3 mb-3">
        <Thumb
          src={missing.imageUrl}
          alt={missing.name}
          size="lg"
          onClick={() =>
            onCompare(selected ?? missing.alternatives[0]?.plant.id ?? null)
          }
          clickLabel={`Compare ${missing.name} with its alternatives`}
        />
        <div className="min-w-0">
          <h3
            id={headingId}
            className="font-display font-bold text-plantista-950 dark:text-white leading-tight"
          >
            {missing.name} is sold out
          </h3>
          {!hasAlternatives && (
            <p className="mt-0.5 text-sm text-plantista-600 dark:text-plantista-300">
              No close match in stock
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {missing.alternatives.map((alt, i) => {
          const id = alt.plant.id;
          const holder = holderOf.get(id);
          const takenElsewhere =
            holder !== undefined && holder !== missing.plantId;
          const checked = selected === id;
          const price = formatPrice(alt.plant.price);
          const inputId = `${groupName}-${id}`;
          return (
            <label key={id} htmlFor={inputId} className={optionRow}>
              <input
                id={inputId}
                type="radio"
                name={groupName}
                value={id}
                checked={checked}
                disabled={takenElsewhere}
                onChange={() => onSelect(missing.plantId, id)}
                className="sr-only"
              />
              <RadioDot checked={checked} />
              <Thumb
                src={alt.plant.imageUrl}
                alt={alt.plant.commonName}
                size="xl"
                onClick={() => onCompare(id)}
                clickLabel={`Compare ${alt.plant.commonName} with ${missing.name}`}
              />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold text-plantista-950 dark:text-white">
                    {alt.plant.commonName}
                  </span>
                  {i === 0 && (
                    <span className="rounded-full bg-plantista-100 dark:bg-plantista-800 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-plantista-700 dark:text-plantista-200">
                      Best match
                    </span>
                  )}
                  {price && (
                    <span className="ml-auto text-sm font-semibold text-plantista-800 dark:text-plantista-100">
                      {price}
                    </span>
                  )}
                </span>
                {alt.reasons.length > 0 && (
                  <span className="mt-0.5 block text-xs text-plantista-600 dark:text-plantista-300">
                    {alt.reasons.join(" · ")}
                  </span>
                )}
                {alt.note && (
                  <span className="mt-0.5 block text-xs italic text-gray-500 dark:text-plantista-400">
                    {alt.note}
                  </span>
                )}
                {takenElsewhere && (
                  <span className="mt-1 flex items-center gap-1 text-xs font-medium text-plantista-700 dark:text-plantista-300">
                    <Info className="w-3.5 h-3.5" aria-hidden="true" />
                    Already chosen for {nameOf(holder)}
                  </span>
                )}
              </span>
            </label>
          );
        })}

        <label htmlFor={`${groupName}-none`} className={optionRow}>
          <input
            id={`${groupName}-none`}
            type="radio"
            name={groupName}
            value=""
            checked={selected === null}
            onChange={() => onSelect(missing.plantId, null)}
            className="sr-only"
          />
          <RadioDot checked={selected === null} />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-plantista-950 dark:text-white">
              Continue without it
            </span>
            <span className="mt-0.5 block text-xs text-plantista-600 dark:text-plantista-300">
              Your design will leave {missing.name} out.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

// ── compare view ────────────────────────────────────────────────────────────

const FACT_ROWS: Array<[keyof PlantFacts, string]> = [
  ["type", "Type"],
  ["sun", "Sun"],
  ["size", "Size"],
  ["flowers", "Flowers"],
  ["foliage", "Foliage"],
  ["shape", "Shape"],
  ["blooms", "Blooms"],
  ["winter", "Winter"],
  ["drought", "Water"],
];

const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();

function ComparePlantCard({
  plant,
  name,
  imageUrl,
  badge,
  tone,
}: {
  plant: DesignPlant | null;
  name: string;
  imageUrl: string | null;
  badge: string;
  tone: "soldout" | "instock";
}) {
  const [failed, setFailed] = useState(false);
  const price = formatPrice(plant?.price ?? null);
  return (
    <div className="min-w-0">
      <div className="relative overflow-hidden rounded-xl bg-plantista-100 dark:bg-plantista-800 aspect-[4/3]">
        {imageUrl && !failed ? (
          // eslint-disable-next-line @next/next/no-img-element -- remote plant images, matches codebase usage
          <img
            src={imageUrl}
            alt={name}
            onError={() => setFailed(true)}
            className={cn(
              "h-full w-full object-cover",
              tone === "soldout" && "opacity-80 grayscale-[35%]"
            )}
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-plantista-500 dark:text-plantista-300"
            role="img"
            aria-label={name}
          >
            <Sprout className="h-8 w-8" aria-hidden="true" />
          </div>
        )}
        <span
          className={cn(
            "absolute left-2 top-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
            tone === "soldout"
              ? "bg-amber-100 text-amber-700 dark:bg-amber-950/80 dark:text-amber-300"
              : "bg-plantista-100 text-plantista-700 dark:bg-plantista-800 dark:text-plantista-200"
          )}
        >
          {badge}
        </span>
      </div>
      <p className="mt-2 font-display font-bold leading-tight text-plantista-950 dark:text-white">
        {name}
      </p>
      {plant?.scientificName && plant.scientificName !== name && (
        <p className="text-xs italic text-plantista-600 dark:text-plantista-300">
          {plant.scientificName}
        </p>
      )}
      {price && (
        <p className="mt-0.5 text-sm font-semibold text-plantista-800 dark:text-plantista-100">
          {price}
        </p>
      )}
    </div>
  );
}

function CompareView({
  titleId,
  titleRef,
  missing,
  alt,
  isSelected,
  takenBy,
  onPickAlt,
  onChoose,
  onClose,
}: {
  titleId: string;
  titleRef: React.RefObject<HTMLHeadingElement | null>;
  missing: MissingPlant;
  alt: AlternativeOption | null;
  isSelected: boolean;
  /** Name of the other sold-out plant this option is already chosen for. */
  takenBy: string | null;
  onPickAlt: (altId: string) => void;
  onChoose: () => void;
  onClose: () => void;
}) {
  const left = missing.facts;
  const right = alt?.facts ?? null;
  const rows = FACT_ROWS.filter(([key]) => left?.[key] || right?.[key]);

  return (
    <>
      <div className="px-6 pt-5 pb-4 border-b border-gray-200 dark:border-plantista-800">
        <button
          type="button"
          onClick={onClose}
          className="mb-2 inline-flex items-center gap-1 rounded text-sm font-medium text-plantista-600 hover:text-plantista-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500 dark:text-plantista-300 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to options
        </button>
        <h2
          id={titleId}
          ref={titleRef}
          tabIndex={-1}
          className="text-xl font-bold font-display text-plantista-950 dark:text-white outline-none"
        >
          Compare with {missing.name}
        </h2>
        {missing.alternatives.length > 1 && (
          <div
            className="mt-3 flex flex-wrap gap-2"
            role="group"
            aria-label="Alternatives to compare"
          >
            {missing.alternatives.map((a, i) => {
              const active = a.plant.id === alt?.plant.id;
              return (
                <button
                  key={a.plant.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onPickAlt(a.plant.id)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plantista-500",
                    active
                      ? "border-plantista-500 bg-plantista-50 text-plantista-900 dark:border-plantista-400 dark:bg-plantista-800 dark:text-white"
                      : "border-gray-200 text-plantista-700 hover:border-plantista-300 dark:border-plantista-700 dark:text-plantista-200 dark:hover:border-plantista-500"
                  )}
                >
                  {a.plant.commonName}
                  {i === 0 && " · Best match"}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <ComparePlantCard
            plant={missing.plant}
            name={missing.name}
            imageUrl={missing.imageUrl}
            badge="Sold out"
            tone="soldout"
          />
          {alt ? (
            <ComparePlantCard
              plant={alt.plant}
              name={alt.plant.commonName}
              imageUrl={alt.plant.imageUrl}
              badge="In stock"
              tone="instock"
            />
          ) : (
            <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 p-4 text-center text-sm text-plantista-600 dark:border-plantista-700 dark:text-plantista-300">
              No close match in stock
            </div>
          )}
        </div>

        {alt && (alt.reasons.length > 0 || alt.note) && (
          <p className="mt-3 text-sm text-plantista-700 dark:text-plantista-200">
            {alt.reasons.join(" · ")}
            {alt.note && (
              <span className="italic text-gray-500 dark:text-plantista-400">
                {alt.reasons.length > 0 ? " · " : ""}
                {alt.note}
              </span>
            )}
          </p>
        )}

        {rows.length > 0 && (
          <dl className="mt-4 divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 text-sm dark:divide-plantista-800 dark:border-plantista-800">
            {rows.map(([key, label]) => {
              const a = left?.[key] ?? null;
              const b = right?.[key] ?? null;
              const same = !!a && !!b && norm(a) === norm(b);
              return (
                <div
                  key={key}
                  className="grid grid-cols-[72px_1fr_1fr] gap-2 px-3 py-2 sm:grid-cols-[96px_1fr_1fr]"
                >
                  <dt className="font-semibold text-plantista-600 dark:text-plantista-300">
                    {label}
                  </dt>
                  <dd className="min-w-0 text-plantista-900 dark:text-plantista-100">
                    {a ?? (
                      <span className="text-gray-400 dark:text-plantista-500">
                        —
                      </span>
                    )}
                  </dd>
                  <dd className="min-w-0 text-plantista-900 dark:text-plantista-100">
                    {alt ? (
                      <span className="inline-flex items-start gap-1">
                        {same && (
                          <CircleCheck
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-plantista-600 dark:text-plantista-400"
                            aria-label="Same"
                          />
                        )}
                        {b ?? (
                          <span className="text-gray-400 dark:text-plantista-500">
                            —
                          </span>
                        )}
                      </span>
                    ) : null}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-plantista-800 bg-white dark:bg-plantista-900 px-6 py-4">
        {takenBy && (
          <p className="mb-3 flex items-center gap-1 text-xs font-medium text-plantista-700 dark:text-plantista-300">
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            Already chosen for {takenBy}
          </p>
        )}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            Back to options
          </Button>
          {alt && (
            <Button
              type="button"
              variant="default"
              onClick={onChoose}
              disabled={!!takenBy}
              className="w-full sm:w-auto font-bold"
            >
              {isSelected ? (
                <>
                  <Check className="w-4 h-4 mr-2" aria-hidden="true" />
                  Keep {alt.plant.commonName}
                </>
              ) : (
                `Choose ${alt.plant.commonName}`
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
